import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { TreatmentLocatorSearchFiltersComponent } from './components/treatment-locator-search-filters/treatment-locator-search-filters.component';
import { TreatmentProviderListItemComponent } from './components/treatment-provider-list-item/treatment-provider-list-item.component';
import { TreatmentLocatorDialog } from './dialogs/treatment-locator-dialog/treatment-locator-dialog.component';
import { FilterService } from './filter.service';
import { LocationService } from './location.service';
import { TreatmentProvider, TreatmentProviderSearchQuery, TreatmentProviderFilterData, TreatmentType, _211ProviderSearchQuery, ServiceKeyword } from './models/TreatmentProviders.models';
import { SupportAndHealthServices, SupportAndHealthServicesListItemComponent } from './components/support-and-health-services-list-item/support-and-health-services-list-item.component';
import { SupportAndHealthServicesDialog } from './dialogs/support-and-health-servies-dialog/support-and-health-services-dialog.component';
import flatten from 'lodash-es/flatten';

@Component({
  templateUrl: './treatment-locator.component.html',
  styleUrls: ['./treatment-locator.component.scss'],
  standalone: true,
  imports: [
    TreatmentLocatorSearchFiltersComponent,
    FormsModule,
    NgIf,
    ReactiveFormsModule,
    NgFor,
    MatProgressSpinner,
    TreatmentProviderListItemComponent,
    SupportAndHealthServicesListItemComponent,
    AsyncPipe,
    TranslateModule
  ]
})
export class TreatmentLocatorComponent implements OnInit {
  readonly TreatmentType = TreatmentType;

  treatmentProviderList$: BehaviorSubject<TreatmentProvider[]> = new BehaviorSubject<TreatmentProvider[]>([]);
  displayedTreatmentProviderList$: BehaviorSubject<TreatmentProvider[]> = new BehaviorSubject<TreatmentProvider[]>([]);

  servicesProviderList$: BehaviorSubject<SupportAndHealthServices[]> = new BehaviorSubject<SupportAndHealthServices[]>([]);
  displayedServicesProviderList$: BehaviorSubject<SupportAndHealthServices[]> = new BehaviorSubject<SupportAndHealthServices[]>([]);


  // MatPaginator Inputs
  length = 0;
  pageSize = 10;
  pageSizeOptions: number[] = [5, 10, 25, 100];

  // MatPaginator Output
  pageEvent!: PageEvent;

  treatmentType = TreatmentType.SubstanceUse;
  treatmentType$ = new BehaviorSubject<TreatmentType>(TreatmentType.SubstanceUse);

  isLoading = false;
  hasSearch = false;

  // @ViewChild(MatAccordion)
  // accordion!: MatAccordion;

  constructor(
    private apiClient: ApiService,
    public dialog: MatDialog,
    public filterService: FilterService,
    public locationService: LocationService
  ) { }

  ngOnInit(): void {
    // this.locationService.requestPermissions()
    //   .subscribe(canUse => {
    //     this.useDeviceLocation = canUse;
    //   });
  }

  onPageChange(pageEvent: PageEvent) {
    this.pageSize = pageEvent.pageSize;

    const pageStart = pageEvent.pageIndex * this.pageSize;
    const pageEnd = Math.min(this.length, this.pageSize + pageStart);

    this.displayedTreatmentProviderList$.next(this.treatmentProviderList$.value.slice(pageStart, pageEnd));
  }

  onClearFilters() {

    this.hasSearch = false;
    this.treatmentProviderList$.next([]);
    this.servicesProviderList$.next([]);
    this.displayedTreatmentProviderList$.next([]);
    this.displayedServicesProviderList$.next([]);
  }

  create211ProviderFilter(filterData: TreatmentProviderFilterData): _211ProviderSearchQuery {
    const services = filterData.servicesOffered.map(x => x.value);
    return {
      Address: filterData.address,
      Latitude: filterData.latitude ?? null,
      Longitude: filterData.longitude ?? null,
      Range: Number(filterData.range),
      TreatmentType: this.treatmentType,
      ServicesOffered: flatten(services)
    };
  }

  createSearchFilter(filterData: TreatmentProviderFilterData): TreatmentProviderSearchQuery {
    const isSupportGroup =
      filterData.agencyType === TreatmentType.HealthServies ||
      filterData.agencyType === TreatmentType.SupportGroups

    let services: ServiceKeyword[][] = []
    if (!isSupportGroup && filterData.servicesOffered)
    services = filterData.servicesOffered.map(x => x.value);


    let demo: string[][] = [];
    if (!isSupportGroup && filterData.demographics)
    demo = filterData.demographics.map(x => x.value);

    let payerOptions: string[] = [];
    if (!isSupportGroup && filterData.payerSources)
    payerOptions = filterData.payerSources.map(x => x.value);

    const serviceKeywords = flatten(services).map(x => x.keyword);
    const demoKeywords = flatten(demo);


    return {
      Address: filterData.address,
      Latitude: filterData.latitude ?? null,
      Longitude: filterData.longitude ?? null,
      Range: filterData.range,
      TreatmentType: filterData.agencyType,
      Demographics: demoKeywords,
      ServicesOffered: serviceKeywords,
      PayerSources: payerOptions
    };
  }


  private async search211Resources(searchData: TreatmentProviderFilterData) {
    const searchFilter = this.create211ProviderFilter(searchData);
    const providers = await this.apiClient.search211Providers(searchFilter).toPromise();
    return providers.filter(x => x.commonName !== null && x.legalName !== null);
  }

  private async searchDefault(searchData: TreatmentProviderFilterData) {
    const searchFilter = this.createSearchFilter(searchData);
    const providers = await this.apiClient.searchTreatmentProviders(searchFilter).toPromise();

    const servicesKeywords: string[] = searchFilter.ServicesOffered
    const demoKeywords: string[] = searchFilter.Demographics;
    const payerSources: string[] = searchFilter.PayerSources

    return this.filterService.filterProvidersByKeywords(providers, demoKeywords, servicesKeywords, payerSources);
  }

  async onApplyFilters(searchData: TreatmentProviderFilterData) {
    this.isLoading = true;
    this.hasSearch = false;

    const handleSuccess = <T>(providers: T[], list$: BehaviorSubject<T[]>) => {
      list$.next(providers);
      this.isLoading = false;
      this.hasSearch = true;
    }

    try {
      if (this.treatmentType === TreatmentType._211Resources) {
        const resources = await this.search211Resources(searchData)
        handleSuccess<TreatmentProvider>(resources, this.treatmentProviderList$)
      } else if (this.treatmentType === TreatmentType.HealthServies || this.treatmentType === TreatmentType.SupportGroups) {
        const searchFilter = this.createSearchFilter(searchData)
        const providers = await this.apiClient.searchSupportAndHealthServices(searchFilter).toPromise()
        handleSuccess<SupportAndHealthServices>(providers, this.servicesProviderList$)
      } else {
        const providers = await this.searchDefault(searchData)
        handleSuccess<TreatmentProvider>(providers, this.treatmentProviderList$)
      }
    } catch (error) {
      console.error(error)
      this.onClearFilters();
      this.isLoading = false;
      this.hasSearch = false;
    }

  }

  radioChange(type: TreatmentType) {
    this.onClearFilters();
    this.treatmentType = type;
    this.treatmentType$.next(type);

    this.isLoading = false;
    this.hasSearch = false;
  }

  openProviderDialog(item: TreatmentProvider) {
    this.dialog.open(TreatmentLocatorDialog, {
      data: item
    });
  }

  openSupportProviderDialog(item: SupportAndHealthServices) {
    this.dialog.open(SupportAndHealthServicesDialog, {
      data: item
    });
  }

  shouldShowServicesOffered(): any {
    return (this.treatmentType != TreatmentType.CrisisCenters &&
      this.treatmentType != TreatmentType.DropOff &&
      this.treatmentType != TreatmentType.Prevention)
  }

  // && this.treatmentType != TreatmentType._211Resources
  shouldShowDemographics(): any {
    return (this.treatmentType != TreatmentType.CrisisCenters &&
      this.treatmentType != TreatmentType.DropOff &&
      this.treatmentType != TreatmentType.Prevention &&
      this.treatmentType != TreatmentType._211Resources)
  }
}


// function isStringList(items: unknown): items is string[] {
//   return Array.isArray(items) && items.every((item) => typeof item === 'string');
// }

